import { create } from 'zustand';
import { useAppStore } from './appStore';
import { useSettingsStore } from './settingsStore';
import { buildApiUrl } from '../utils/serverOrigin';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TeamMessageType = 'human' | 'agent_call' | 'agent_reply';

export interface TeamMessageToolCall {
  toolCallId: string;
  name: string;
  status: 'running' | 'done' | 'error';
  input?: string;
  output?: string;
}

export interface TeamMessage {
  id: string;
  ts: number;
  sender: string;
  type: TeamMessageType;
  content: string;
  agentId?: string;
  agentName?: string;
  sessionKey?: string;
  replyToId?: string;
  thinkingContent?: string;
  toolCalls?: TeamMessageToolCall[];
}

export interface ActiveAgentCallToolCall {
  toolCallId: string;
  name: string;
  status: 'running' | 'done' | 'error';
  input?: string;
  output?: string;
}

export interface ActiveAgentCall {
  sessionKey: string;
  streaming: boolean;
  streamText: string;
  startTime: number;
  thinkingText?: string;
  toolCalls?: ActiveAgentCallToolCall[];
}

interface TeamChannelState {
  messages: TeamMessage[];
  isLoading: boolean;
  hasMore: boolean;
  newestId: string | null;
  oldestId: string | null;

  // Agent call state (keyed by callMessageId)
  activeAgentCalls: Record<string, ActiveAgentCall>;

  // Popup state
  popupSessionKey: string | null;
  popupAgentName: string | null;
  popupReplyId: string | null;

  // Settings
  contextMessageLimit: number;
  agentReplyMaxLength: number;

  // Actions
  loadInitial: () => Promise<void>;
  loadMore: () => Promise<void>;
  pollNew: () => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  sendAgentCall: (agentId: string, agentName: string, content: string) => Promise<{ callId: string; sessionKey: string }>;
  saveAgentReply: (callId: string, agentId: string, agentName: string, sessionKey: string, content: string) => Promise<void>;
  openPopup: (sessionKey: string, agentName: string, replyId: string) => void;
  closePopup: () => void;
  setActiveAgentCallStreaming: (callId: string, text: string) => void;
  setActiveAgentCallThinking: (callId: string, thinkingText: string) => void;
  setActiveAgentCallToolCall: (callId: string, toolCall: ActiveAgentCallToolCall) => void;
  setActiveAgentCallDone: (callId: string) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function randomTeamId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const arr = new Uint8Array(12);
  crypto.getRandomValues(arr);
  for (const byte of arr) {
    result += chars[byte % chars.length];
  }
  return result;
}

function getGatewayToken(): string {
  return useAppStore.getState().gatewayToken;
}

function getSenderName(): string {
  const displayName = useSettingsStore.getState().settings.general.displayName.trim();
  if (displayName) return displayName;
  // Fall back to stored display name from various localStorage keys
  try {
    const stored = localStorage.getItem('clawlet.displayName');
    if (stored) return stored;
    const alt = localStorage.getItem('displayName') ?? localStorage.getItem('clawlet.userName');
    if (alt) return alt;
  } catch { /* ignore */ }
  return 'User';
}

async function apiSend(body: Record<string, unknown>): Promise<{ id: string; ts: number }> {
  const token = getGatewayToken();
  const res = await fetch(buildApiUrl('/api/team/send', useAppStore.getState().gatewayUrl), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API error ${res.status}: ${text}`);
  }
  return res.json() as Promise<{ id: string; ts: number }>;
}

async function apiHistory(params: Record<string, string | number>): Promise<{ messages: TeamMessage[]; hasMore: boolean }> {
  const token = getGatewayToken();
  const qs = new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)])
  ).toString();
  const res = await fetch(buildApiUrl(`/api/team/history?${qs}`, useAppStore.getState().gatewayUrl), {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API error ${res.status}: ${text}`);
  }
  return res.json() as Promise<{ messages: TeamMessage[]; hasMore: boolean }>;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useTeamChannelStore = create<TeamChannelState>((set, get) => ({
  messages: [],
  isLoading: false,
  hasMore: true,
  newestId: null,
  oldestId: null,
  activeAgentCalls: {},
  popupSessionKey: null,
  popupAgentName: null,
  popupReplyId: null,
  contextMessageLimit: 20,
  agentReplyMaxLength: 300,

  loadInitial: async () => {
    set({ isLoading: true });
    try {
      const { messages, hasMore } = await apiHistory({ limit: 20 });
      set({
        messages,
        hasMore,
        newestId: messages.length > 0 ? messages[messages.length - 1].id : null,
        oldestId: messages.length > 0 ? messages[0].id : null,
        isLoading: false,
      });
    } catch (err) {
      console.error('[teamChannelStore] loadInitial error:', err);
      set({ isLoading: false });
    }
  },

  loadMore: async () => {
    const { oldestId, hasMore, isLoading } = get();
    if (!hasMore || isLoading || !oldestId) return;
    set({ isLoading: true });
    try {
      const { messages: older, hasMore: moreRemain } = await apiHistory({ limit: 20, before: oldestId });
      set((state) => ({
        messages: [...older, ...state.messages],
        hasMore: moreRemain,
        oldestId: older.length > 0 ? older[0].id : state.oldestId,
        isLoading: false,
      }));
    } catch (err) {
      console.error('[teamChannelStore] loadMore error:', err);
      set({ isLoading: false });
    }
  },

  pollNew: async () => {
    const { newestId } = get();
    const params: Record<string, string | number> = {};
    if (newestId) params.after = newestId;
    else params.limit = 20;

    try {
      const { messages: newMsgs } = await apiHistory(params);
      if (newMsgs.length > 0) {
        set((state) => {
          // Merge: avoid duplicates by id
          const existingIds = new Set(state.messages.map((m) => m.id));
          const novel = newMsgs.filter((m) => !existingIds.has(m.id));
          if (novel.length === 0) return state;
          const combined = [...state.messages, ...novel];
          return {
            messages: combined,
            newestId: combined[combined.length - 1].id,
          };
        });
        // Browser notification for messages from other users
        const senderName = getSenderName();
        const fromOthers = newMsgs.filter((m) => m.sender !== senderName && m.type !== 'agent_reply');
        if (fromOthers.length > 0 && document.hidden) {
          const last = fromOthers[fromOthers.length - 1];
          try {
            if (Notification.permission === 'granted') {
              new Notification('Team Channel', { body: `${last.sender}: ${last.content.slice(0, 80)}` });
            }
          } catch { /* ignore */ }
        }
      }
    } catch (err) {
      console.warn('[teamChannelStore] pollNew error:', err);
    }
  },

  sendMessage: async (content: string) => {
    const sender = getSenderName();
    // Optimistic UI: add immediately
    const optimisticId = `opt-${Date.now()}`;
    const optimisticMsg: TeamMessage = {
      id: optimisticId,
      ts: Date.now(),
      sender,
      type: 'human',
      content,
    };
    set((state) => ({
      messages: [...state.messages, optimisticMsg],
      newestId: optimisticId,
    }));

    try {
      const { id, ts } = await apiSend({ sender, type: 'human', content });
      // Replace optimistic with real
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === optimisticId ? { ...m, id, ts } : m
        ),
        newestId: id,
      }));
    } catch (err) {
      console.error('[teamChannelStore] sendMessage error:', err);
      // Remove optimistic on error
      set((state) => ({
        messages: state.messages.filter((m) => m.id !== optimisticId),
      }));
    }
  },

  sendAgentCall: async (agentId: string, agentName: string, content: string) => {
    const sender = getSenderName();
    // Save agent_call message to JSONL
    const { id: callId } = await apiSend({
      sender,
      type: 'agent_call',
      content,
      agentId,
      agentName,
    });

    // Add to local messages immediately
    const callMsg: TeamMessage = {
      id: callId,
      ts: Date.now(),
      sender,
      type: 'agent_call',
      content,
      agentId,
      agentName,
    };
    set((state) => ({
      messages: [...state.messages.filter((m) => m.id !== callId), callMsg],
      newestId: callId,
    }));

    // Generate session key and initialize active agent call state
    const sessionKey = `agent:${agentId}:team-${randomTeamId()}`;
    set((state) => ({
      activeAgentCalls: {
        ...state.activeAgentCalls,
        [callId]: { sessionKey, streaming: true, streamText: '', startTime: Date.now() },
      },
    }));

    // Return callId and sessionKey so the component can handle the actual gateway send
    // (Component has access to sendMessage via useGateway hook)
    return { callId, sessionKey };
  },

  saveAgentReply: async (callId: string, agentId: string, agentName: string, sessionKey: string, content: string) => {
    // Extract thinking/toolCalls from active call BEFORE removing it
    const activeCall = get().activeAgentCalls[callId];
    const thinkingContent = activeCall?.thinkingText || undefined;
    const toolCalls = activeCall?.toolCalls && activeCall.toolCalls.length > 0
      ? [...activeCall.toolCalls]
      : undefined;

    // Remove active call
    set((state) => {
      const next = { ...state.activeAgentCalls };
      delete next[callId];
      return { activeAgentCalls: next };
    });

    try {
      const { id, ts } = await apiSend({
        sender: agentName,
        type: 'agent_reply',
        content,
        agentId,
        agentName,
        sessionKey,
        replyToId: callId,
        thinkingContent,
        toolCalls,
      });

      const replyMsg: TeamMessage = {
        id,
        ts,
        sender: agentName,
        type: 'agent_reply',
        content,
        agentId,
        agentName,
        sessionKey,
        replyToId: callId,
        thinkingContent,
        toolCalls,
      };

      set((state) => ({
        messages: [...state.messages, replyMsg],
        newestId: id,
      }));
    } catch (err) {
      console.error('[teamChannelStore] saveAgentReply error:', err);
    }
  },

  setActiveAgentCallStreaming: (callId: string, text: string) => {
    set((state) => {
      const call = state.activeAgentCalls[callId];
      if (!call) return state;
      return {
        activeAgentCalls: {
          ...state.activeAgentCalls,
          [callId]: { ...call, streaming: true, streamText: text },
        },
      };
    });
  },

  setActiveAgentCallThinking: (callId: string, thinkingText: string) => {
    set((state) => {
      const call = state.activeAgentCalls[callId];
      if (!call) return state;
      return {
        activeAgentCalls: {
          ...state.activeAgentCalls,
          [callId]: { ...call, thinkingText },
        },
      };
    });
  },

  setActiveAgentCallToolCall: (callId: string, toolCall: ActiveAgentCallToolCall) => {
    set((state) => {
      const call = state.activeAgentCalls[callId];
      if (!call) return state;
      const existing = call.toolCalls ?? [];
      const idx = existing.findIndex((tc) => tc.toolCallId === toolCall.toolCallId);
      const updated = idx >= 0
        ? existing.map((tc, i) => i === idx ? toolCall : tc)
        : [...existing, toolCall];
      return {
        activeAgentCalls: {
          ...state.activeAgentCalls,
          [callId]: { ...call, toolCalls: updated },
        },
      };
    });
  },

  setActiveAgentCallDone: (callId: string) => {
    set((state) => {
      const call = state.activeAgentCalls[callId];
      if (!call) return state;
      return {
        activeAgentCalls: {
          ...state.activeAgentCalls,
          [callId]: { ...call, streaming: false },
        },
      };
    });
  },

  openPopup: (sessionKey: string, agentName: string, replyId: string) => {
    set({ popupSessionKey: sessionKey, popupAgentName: agentName, popupReplyId: replyId });
  },

  closePopup: () => {
    set({ popupSessionKey: null, popupAgentName: null, popupReplyId: null });
  },
}));
